<script setup>
import {
  ref,
  watch,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },
  maxHeightClass: {
    type: String,
    default: 'max-h-[9.75rem]',
  },
});

const emit = defineEmits(['mentionSelect']);

// Gap kept between the top of the dropdown and the top of the viewport.
const VIEWPORT_GAP = 8;

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);

// The box is anchored with `bottom-full`, so it grows upwards and can run past
// the top of the viewport when the editor sits high on the screen (e.g. the new
// conversation modal). Cap its height to the space actually available above it.
const availableHeight = ref(null);
// max-height coming from `maxHeightClass`, read once before we override it.
let intrinsicMaxHeight = null;
let anchorResizeObserver = null;

const boxStyle = computed(() => {
  if (availableHeight.value === null) return {};
  return {
    maxHeight: `${Math.min(intrinsicMaxHeight, availableHeight.value)}px`,
  };
});

const updateAvailableHeight = () => {
  const container = mentionsListContainerRef.value;
  if (!container) return;

  if (intrinsicMaxHeight === null) {
    const parsed = Number.parseFloat(getComputedStyle(container).maxHeight);
    intrinsicMaxHeight = Number.isNaN(parsed) ? Infinity : parsed;
  }

  // The bottom edge is pinned to the top of the anchor, so it stays put while
  // the height changes — measuring it here cannot loop back on itself.
  const { bottom } = container.getBoundingClientRect();
  availableHeight.value = Math.max(bottom - VIEWPORT_GAP, 0);
};

const adjustScroll = () => {
  const container = mentionsListContainerRef.value;
  const item = container.querySelector(`#mention-item-${selectedIndex.value}`);
  if (item) {
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.offsetHeight;
    if (itemTop < containerTop) {
      container.scrollTop = itemTop;
    } else if (itemBottom + 34 > containerBottom) {
      container.scrollTop = itemBottom - container.offsetHeight + 34;
    }
  }
};

const onSelect = () => {
  emit('mentionSelect', props.items[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => props.items),
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => props.items,
  () => {
    selectedIndex.value = 0;
    nextTick(() => {
      if (mentionsListContainerRef.value) {
        mentionsListContainerRef.value.scrollTop = 0;
      }
      updateAvailableHeight();
    });
  }
);

onMounted(() => {
  nextTick(updateAvailableHeight);
  window.addEventListener('resize', updateAvailableHeight);

  // The anchor (editor / textarea) grows as the user types, which moves the
  // dropdown down and frees up space above it.
  const anchor = mentionsListContainerRef.value?.offsetParent;
  if (anchor && window.ResizeObserver) {
    anchorResizeObserver = new ResizeObserver(updateAvailableHeight);
    anchorResizeObserver.observe(anchor);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateAvailableHeight);
  anchorResizeObserver?.disconnect();
  anchorResizeObserver = null;
});

watch(selectedIndex, adjustScroll);

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  if (props.type === 'variable') return `{{${item.label}}}`;
  if (props.type === 'command') return `!${item.label}`;
  return `/${item.label}`;
};
</script>

<template>
  <div
    ref="mentionsListContainerRef"
    class="bg-n-solid-1 p-1 rounded-xl overflow-auto absolute w-full z-20 shadow-md left-0 bottom-full border border-solid border-n-strong mention--box"
    :class="maxHeightClass"
    :style="boxStyle"
  >
    <ul class="mb-0 vertical dropdown menu">
      <woot-dropdown-item
        v-for="(item, index) in items"
        :id="`mention-item-${index}`"
        :key="item.key"
        class="!mb-1"
      >
        <button
          class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
          :class="{
            'bg-n-alpha-black2': index === selectedIndex,
          }"
          @click="onListItemSelection(index)"
        >
          <slot :item="item" :index="index" :selected="index === selectedIndex">
            <p
              class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-11 text-ellipsis whitespace-nowrap"
              :class="{
                'text-n-slate-12': index === selectedIndex,
              }"
            >
              {{ item.description }}
            </p>
            <p
              class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-slate-500 dark:text-slate-300 group-hover:text-n-slate-11 text-ellipsis whitespace-nowrap"
              :class="{
                'text-n-slate-12': index === selectedIndex,
              }"
            >
              {{ variableKey(item) }}
            </p>
          </slot>
        </button>
      </woot-dropdown-item>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
